import React from 'react'
import {
	Box,
	Typography,
	TextField,
	IconButton,
	Stack,
	Divider,
	CircularProgress,
	alpha,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { ComponentChangeOrder } from '../../../types/Work/Survey'
import { ImageAttachmentDialog } from '../../core/ImageAttachmentDialog'
import {
	deleteChangeOrderAttachment,
	getChangeOrderAttachments,
} from '../../../services/quantitySurveyService'

type ComponentInspectionFormProps = {
	component: ComponentChangeOrder
	onValueChange: (
		component: ComponentChangeOrder,
		newValue: number | null,
		blobs?: Blob[],
	) => Promise<void>
	isEditable: boolean
	surveyCompleted: boolean
}

export function ComponentInspectionForm({
	component,
	onValueChange,
	isEditable,
	surveyCompleted,
}: ComponentInspectionFormProps) {
	const [isEditing, setIsEditing] = React.useState(false)
	const [tempValue, setTempValue] = React.useState<string>(
		component.proposedValue?.toString() ?? component.originalValue.toString(),
	)
	const [valueUpdating, setValueUpdating] = React.useState(false)

	const handleEdit = () => {
		setIsEditing(true)
	}

	const handleCancel = () => {
		setTempValue(component.proposedValue?.toString() ?? component.originalValue.toString())
		setIsEditing(false)
	}

	const handleSave = async (blobs?: Blob[]) => {
		setValueUpdating(true)
		const numValue = parseFloat(tempValue)
		if (!isNaN(numValue)) {
			await onValueChange(component, numValue, blobs)
		}
		setIsEditing(false)
		setValueUpdating(false)
	}

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleSave()
		} else if (e.key === 'Escape') {
			handleCancel()
		}
	}

	return (
		<Box
			sx={{
				p: 2,
				width: '100%',
				bgcolor: 'white',
				borderRadius: 1,
				border: '1px solid',
				borderColor: 'divider',
				'&:hover': {
					boxShadow: isEditable ? 1 : 0,
				},
			}}
		>
			<Stack spacing={2}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography
						variant='subtitle1'
						color='primary'
						sx={{
							fontWeight: 500,
							display: 'flex',
							alignItems: 'center',
							gap: 1,
						}}
					>
						{component.componentType}
						{isEditable && !isEditing && !surveyCompleted && (
							<IconButton
								size='medium'
								onClick={handleEdit}
								sx={{
									color: 'primary.main',
									opacity: 0.7,
									'&:hover': {
										opacity: 1,
									},
								}}
							>
								<EditIcon fontSize='medium' />
							</IconButton>
						)}
					</Typography>
					{(!surveyCompleted || component.hasAttachments) && (
						<ImageAttachmentDialog
							onSave={handleSave}
							getAttachments={() =>
								component.changeOrderComponentId ? getChangeOrderAttachments(component.changeOrderComponentId) : null
							}
							deleteAttachment={(attachmentId: number) =>
								component.changeOrderComponentId
									? deleteChangeOrderAttachment(component.changeOrderComponentId, attachmentId)
									: null
							}
							title='Change Order Attachment'
							readOnly={surveyCompleted}
						/>
					)}
				</Box>

				<Stack spacing={1.5}>
					<Box>
						<Typography variant='caption' color='text.secondary' sx={{ mb: 0.5, display: 'block' }}>
							Original Value
						</Typography>
						<TextField
							fullWidth
							size='small'
							value={component.originalValue}
							disabled
							sx={{
								'& .MuiInputBase-root': {
									bgcolor: alpha('#f5f5f5', 0.7),
								},
							}}
							InputProps={{
								endAdornment: (
									<Typography variant='body2' color='text.secondary'>
										{component.unitOfMeasure}
									</Typography>
								),
							}}
						/>
					</Box>

					<Box>
						<Typography variant='caption' color='text.secondary' sx={{ mb: 0.5, display: 'block' }}>
							{isEditable ? 'Proposed Value' : 'Current Value'}
						</Typography>
						<Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
							<TextField
								fullWidth
								size='small'
								value={tempValue}
								onChange={(e) => setTempValue(e.target.value)}
								disabled={!isEditing}
								error={isEditing && isNaN(parseFloat(tempValue))}
								helperText={
									isEditing && isNaN(parseFloat(tempValue)) ? 'Please enter a valid number' : ''
								}
								onKeyDown={handleKeyPress}
								sx={{
									'& .MuiInputBase-root': {
										bgcolor: isEditing ? 'white' : alpha('#f5f5f5', 0.7),
									},
								}}
								InputProps={{
									endAdornment: (
										<Typography variant='body2' color='text.secondary'>
											{component.unitOfMeasure}
										</Typography>
									),
								}}
							/>
							{isEditing && (
								<Stack direction='row' spacing={0.5} sx={{ mt: 0.5 }}>
									{valueUpdating ? (
										<CircularProgress size={20} sx={{ m: 0 }} />
									) : (
										<>
											<IconButton
												size='small'
												onClick={() => handleSave()}
												color='primary'
												disabled={isNaN(parseFloat(tempValue))}
											>
												<CheckIcon fontSize='small' />
											</IconButton>

											<IconButton size='small' onClick={handleCancel} color='error'>
												<CloseIcon fontSize='small' />
											</IconButton>
										</>
									)}
								</Stack>
							)}
						</Box>
					</Box>
				</Stack>

				{component.proposedValue !== null &&
					component.proposedValue !== component.originalValue && (
						<Box
							sx={{
								mt: 2,
								py: 0.75,
								px: 1.5,
								bgcolor: alpha('#4caf50', 0.05),
								borderRadius: 0.5,
								border: '1px solid',
								borderColor: alpha('#4caf50', 0.2),
								display: 'flex',
								alignItems: 'center',
								gap: 0.5,
							}}
						>
							<Typography variant='caption' color='success.main' sx={{ fontWeight: 500 }}>
								Changed: {component.originalValue} → {component.proposedValue}{' '}
								{component.unitOfMeasure}
							</Typography>
						</Box>
					)}
			</Stack>
		</Box>
	)
}
